/**
 * HH:MM ⇄ decimal string helpers for payroll-hour cells.
 *
 * Design note
 * -----------
 * Source documents sometimes express hours as ``HH:MM`` (e.g. ``90:31``).
 * The Core API only accepts numeric hours, and its own internal contract
 * treats the decimal portion as minutes (``40.15`` → ``40 hours 15 minutes``).
 * So the LLM (and this module) perform a *lexical* swap — colon ⇄ period —
 * and never compute ``minutes / 60``. The boolean ``isHoursMinutes`` flag
 * travels alongside the value so the grid can round-trip display back to
 * the original colon form.
 *
 * The numeric value stored on ``PayItemValue.value`` is always the
 * colon-swapped decimal *string* (e.g. ``"40.15"``) — we keep it a string
 * so ``40:5`` and ``40:50`` stay distinguishable (``"40.5"`` vs ``"40.50"``)
 * all the way down to ``parseFloat`` at submit time.
 */

export interface ParsedPayItemInput {
  /** Decimal string suitable for storage (e.g. ``"40.15"``). ``null`` for empty input. */
  value: string | null;
  /** ``true`` when the user's input contained a colon, signalling HH:MM form. */
  isHoursMinutes: boolean;
}

/**
 * Format a stored value for display.
 *
 * When ``isHoursMinutes`` is ``true`` and the stored value contains a period,
 * the first period is swapped for a colon (``"40.15"`` → ``"40:15"``,
 * ``"40.5"`` → ``"40:5"``). Pure integers like ``"40"`` stay unchanged — a
 * display of ``40:`` would be misleading.
 *
 * When the flag is ``false`` (or undefined) the raw value is returned as-is.
 */
export function formatPayItemValue(
  value: string | null | undefined,
  isHoursMinutes: boolean | undefined
): string {
  if (value === null || value === undefined || value === '') {
    return '';
  }
  if (!isHoursMinutes) {
    return value;
  }
  const periodIdx = value.indexOf('.');
  if (periodIdx === -1) {
    return value;
  }
  return `${value.slice(0, periodIdx)}:${value.slice(periodIdx + 1)}`;
}

/**
 * Parse a raw textbox input into the storage shape.
 *
 * - A colon anywhere in the input flips ``isHoursMinutes`` to ``true`` and the
 *   first colon is swapped for a period.
 * - Plain decimal / integer input leaves ``isHoursMinutes`` ``false``.
 * - Leading/trailing whitespace is trimmed.
 * - Empty input returns ``{ value: null, isHoursMinutes: false }`` so the
 *   caller can clear the cell.
 *
 * We deliberately do not validate that the minute portion is two digits or
 * in the 0-59 range — the rule with the user is that we preserve whatever
 * they type verbatim, swap the colon, and let the Core API be the ultimate
 * arbiter.
 */
export function parsePayItemInput(raw: string): ParsedPayItemInput {
  const trimmed = (raw ?? '').trim();
  if (trimmed === '') {
    return { value: null, isHoursMinutes: false };
  }

  const colonIdx = trimmed.indexOf(':');
  if (colonIdx === -1) {
    return { value: trimmed, isHoursMinutes: false };
  }

  const hh = trimmed.slice(0, colonIdx);
  const mm = trimmed.slice(colonIdx + 1);
  return {
    value: `${hh}.${mm}`,
    isHoursMinutes: true,
  };
}

/** A single cell participating in a column-total calculation. */
export interface HoursTotalCell {
  value: string | null;
  isHoursMinutes: boolean | undefined;
}

/**
 * Convert one HH:MM-flagged stored value to a whole-minute count.
 *
 * Works off the stored *string* (not a float), so ``"40.5"`` and ``"40.50"``
 * stay distinct — important because once the colon was stripped at store
 * time they carry different minute values.
 */
function hhMmStringToMinutes(value: string): number {
  const periodIdx = value.indexOf('.');
  if (periodIdx === -1) {
    const hrs = parseInt(value, 10);
    return Number.isFinite(hrs) ? hrs * 60 : 0;
  }
  const hours = parseInt(value.slice(0, periodIdx) || '0', 10) || 0;
  const minutes = parseInt(value.slice(periodIdx + 1) || '0', 10) || 0;
  return hours * 60 + minutes;
}

/**
 * Sum a column of hour-type cells, returning a display string.
 *
 * - If any cell in the column is HH:MM-flagged the whole column totals in
 *   HH:MM form (minute-accurate, carrying over 60-minute boundaries). Both
 *   HH:MM-flagged and plain-decimal cells are summed as minutes so the total
 *   is mathematically correct.
 * - Otherwise the caller's existing decimal behaviour applies — this helper
 *   returns ``null`` and the caller formats the returned ``decimalTotal``.
 */
export function sumHoursColumn(cells: HoursTotalCell[]): {
  display: string;
  decimalTotal: number;
  isHoursMinutes: boolean;
} {
  const anyHhMm = cells.some((c) => c.isHoursMinutes === true);

  if (!anyHhMm) {
    const decimal = cells.reduce((acc, c) => acc + (parseFloat(c.value || '') || 0), 0);
    return {
      display: decimal.toFixed(2),
      decimalTotal: decimal,
      isHoursMinutes: false,
    };
  }

  let totalMinutes = 0;
  for (const c of cells) {
    if (!c.value) continue;
    if (c.isHoursMinutes) {
      totalMinutes += hhMmStringToMinutes(c.value);
    } else {
      // Plain decimal hours — convert to minutes for a mathematically correct
      // mixed-format sum. Rounded to the nearest minute because HH:MM has no
      // sub-minute resolution.
      const decimal = parseFloat(c.value) || 0;
      totalMinutes += Math.round(decimal * 60);
    }
  }

  const totalHours = Math.trunc(totalMinutes / 60);
  const leftover = totalMinutes % 60;
  return {
    display: `${totalHours}:${String(leftover).padStart(2, '0')}`,
    decimalTotal: totalMinutes / 60,
    isHoursMinutes: true,
  };
}
